import {
  EVENT_SOURCES,
  INBOX_GROUPS,
  INBOX_IMPORTANCE_FILTERS,
  type InboxFilter,
  type InboxGroup,
  type InboxImportance,
  type InboxItemDto,
  type ListInboxInput,
} from '@fos/shared/contracts/inbox';
import type { EventSource, Severity } from '@fos/shared/enums';
import {
  EVENT_SOURCE_LABELS,
  INBOX_FILTER_LABELS,
  INBOX_GROUP_LABELS,
  INBOX_IMPORTANCE_LABELS,
  MAIL_IMPORTANCE_LABELS,
  pendingApprovalsBanner,
  pendingApprovalsShort,
  SEVERITY_LABELS,
  snoozedCountLabel,
} from '@fos/shared/labels';
import { Inbox, MoreHorizontal, Search, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Sheet } from '../../../ui/Dialog';
import { ClientChip } from '../../../ui/EntityLabel';
import { Field } from '../../../ui/Field';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { Dot, Status } from '../../../ui/Status';
import { StatusPill } from '../../../ui/StatusPill';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { TOUCH_BUTTON, useIsPhone } from '../../mobile';
import { useInboxActions, useInboxCounts } from '../hooks';
import {
  EMPTY_LIST_TEXT,
  FILTER_SEVERITIES,
  isLessImportantMail,
  listTime,
  senderLine,
  snoozedUntilLabel,
} from '../inbox-format';

const PAGE = 50;
const ALL = 'all';

/** Zakładki w adresie: `?widok=nieprzeczytane|zrobione|odlozone` (domyślnie „Wszystkie”). */
const VIEW_PARAM: Record<string, InboxFilter> = {
  nieprzeczytane: 'unread',
  zrobione: 'done',
  odlozone: 'snoozed',
};
const PARAM_OF_VIEW: Partial<Record<InboxFilter, string>> = {
  unread: 'nieprzeczytane',
  done: 'zrobione',
  snoozed: 'odlozone',
};

export interface InboxFilters {
  filter: InboxFilter;
  /** „Istotne” chowa mniej ważną pocztę (`?poczta=istotne`; 17.10.2026) */
  importance: InboxImportance;
  severity: Severity | null;
  clientId: string | null;
  source: EventSource | null;
}

/** Filtry z adresu: `?widok=…&poczta=istotne&waznosc=critical&klient=<id>&zrodlo=monitor`. */
export function inboxFiltersFromParams(params: URLSearchParams): InboxFilters {
  const severity = params.get('waznosc');
  const source = params.get('zrodlo');
  return {
    filter: VIEW_PARAM[params.get('widok') ?? ''] ?? 'open',
    importance: params.get('poczta') === 'istotne' ? 'important' : 'all',
    severity: (FILTER_SEVERITIES as string[]).includes(severity ?? '') ? (severity as Severity) : null,
    clientId: params.get('klient'),
    source: (EVENT_SOURCES as readonly string[]).includes(source ?? '') ? (source as EventSource) : null,
  };
}

export function inboxListInput(f: InboxFilters, limit = PAGE): ListInboxInput {
  return {
    filter: f.filter,
    limit,
    ...(f.importance === 'important' ? { importance: 'important' as const } : {}),
    ...(f.severity ? { severity: f.severity } : {}),
    ...(f.clientId ? { clientId: f.clientId } : {}),
    ...(f.source ? { source: f.source } : {}),
  };
}

function extraFilterCount(f: InboxFilters): number {
  return [f.importance === 'important', f.severity, f.clientId, f.source].filter(Boolean).length;
}

/** Wiersz listy (Figma 02 / DESIGN §5): nieprzeczytany — niebieska kropka, nadawca i czas Medium, temat Semi Bold. */
export function InboxRow({ item, active, to, now }: { item: InboxItemDto; active: boolean; to: string; now: Date }) {
  const unread = item.status === 'unread';
  const e = item.event;
  const snoozed = item.snoozedUntil && item.snoozedUntil.getTime() > now.getTime() ? item.snoozedUntil : null;
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex flex-col gap-[3px] border-b border-line py-3.5 transition-colors md:rounded-lg md:border-0 md:px-3 md:py-2.5',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
        active ? 'md:bg-elevated' : 'md:hover:bg-surface',
      )}
    >
      <span className={cn('flex items-center gap-2 text-caption', unread ? 'font-medium text-fg' : 'text-fg-muted')}>
        {unread && <Dot tone="accent" />}
        {unread && <span className="sr-only">Nieprzeczytane: </span>}
        {/* lifting 17.10.2026: klient jako chip w jego barwie (zamiast „· klient” w tekście) */}
        <span className="min-w-0 truncate">{e.client ? senderLine({ ...e, client: null }) : senderLine(e)}</span>
        {e.client && <ClientChip client={e.client} className="font-medium" />}
        <span className="flex-1" />
        {e.simulated && <Status tone="warning">Symulacja</Status>}
        {isLessImportantMail(e) && (
          <StatusPill tone="neutral" size="s" className="shrink-0">
            {MAIL_IMPORTANCE_LABELS.less}
          </StatusPill>
        )}
        <span className="shrink-0 tabular">{listTime(e.occurredAt, now)}</span>
      </span>
      <span className={cn('truncate text-body-s', unread ? 'font-semibold text-fg' : 'text-fg-secondary')}>
        {e.title}
      </span>
      {snoozed && <span className="text-caption text-fg-muted">Odłożone {snoozedUntilLabel(snoozed, now)}</span>}
    </Link>
  );
}

function GroupLabel({ group }: { group: InboxGroup }) {
  return (
    <h2 className="flex items-center gap-2 pt-3.5 pb-1.5 text-caption font-medium text-fg-muted md:pl-3">
      {group === 'urgent' && <Dot tone="danger" size={6} />}
      {INBOX_GROUP_LABELS[group]}
    </h2>
  );
}

/** Baner „3 akcje AI czekają na akceptację” (Figma 02) — z modułem Akceptacje i czekającymi akcjami. */
function ApprovalsBanner() {
  const access = useAccess();
  const enabled = access.can('approvals');
  const query = trpc.today.pendingApprovals.useQuery({ decidableOnly: true, limit: 1 }, { enabled });
  const data = query.data;
  if (!enabled || !data || data.total === 0) return null;
  const n = data.total;
  return (
    <ContextBanner tone="accent" icon={ShieldCheck} linkTo="/akceptacje" className="mb-2">
      <span className="md:hidden">{pendingApprovalsShort(n, { ai: data.allByAi })}</span>
      <span className="hidden md:inline">{pendingApprovalsBanner(n, { ai: data.allByAi })}</span>
    </ContextBanner>
  );
}

/**
 * Lewa kolumna Inboxu (Figma 02 „List”, 360 px; mobile — cała strona): tytuł z menu „…”, baner akceptacji, zakładki
 * Wszystkie / Nieprzeczytane / Zrobione, filtry (ważność, klient, źródło — w adresie), grupy Pilne / Nowe /
 * Wcześniej, „Pokaż starsze”, link do odłożonych.
 */
export function InboxList({
  selectedId,
  headingLevel = 'h1',
  menu,
}: {
  selectedId: string | null;
  headingLevel?: 'h1' | 'h2';
  /** dodatkowe pozycje menu „…” (np. „Symuluj zdarzenie” w trybie deweloperskim) */
  menu?: ReactNode;
}) {
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const filters = inboxFiltersFromParams(params);
  const [limit, setLimit] = useState(PAGE);
  const [filtersOpen, setFiltersOpen] = useState(() => extraFilterCount(filters) > 0);
  const now = new Date();
  const search = params.toString();
  const actions = useInboxActions();
  const counts = useInboxCounts();

  const input = inboxListInput(filters, limit);
  const list = trpc.inbox.list.useQuery(input, {
    // „Pokaż starsze” (większy limit) zostawia dotychczasowe pozycje; inny filtr — nowa lista
    placeholderData: (prev, prevQuery) => {
      const key = prevQuery?.queryKey as unknown[] | undefined;
      const prevInput = (key?.[1] as { input?: ListInboxInput } | undefined)?.input;
      const { limit: _a, ...a } = prevInput ?? {};
      const { limit: _b, ...b } = input ?? {};
      return JSON.stringify(a) === JSON.stringify(b) ? prev : undefined;
    },
  });

  const setParam = (key: string, value: string | null) => {
    setLimit(PAGE);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value) p.set(key, value);
        else p.delete(key);
        return p;
      },
      { replace: true },
    );
  };
  const setView = (f: InboxFilter) => setParam('widok', PARAM_OF_VIEW[f] ?? null);
  const clearFilters = () => {
    setLimit(PAGE);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const k of ['poczta', 'waznosc', 'klient', 'zrodlo']) p.delete(k);
        return p;
      },
      { replace: true },
    );
  };

  const Heading = headingLevel;
  const extra = extraFilterCount(filters);
  const segmentValue: InboxFilter = filters.filter === 'snoozed' ? 'open' : filters.filter;
  const itemPath = (id: string) => `/inbox/${id}${search ? `?${search}` : ''}`;
  const unreadCount = counts.data?.unread ?? 0;

  let body: ReactNode;
  if (list.isPending) body = <ListSkeleton rows={6} className="md:px-3" />;
  else if (list.isError)
    body = (
      <ErrorState
        compact
        title="Nie udało się wczytać Inboxu"
        onRetry={() => void list.refetch()}
        retrying={list.isFetching}
      />
    );
  else if (list.data.items.length === 0) {
    const text = EMPTY_LIST_TEXT[filters.filter];
    body =
      extra > 0 ? (
        <EmptyState
          icon={Search}
          title="Nic nie pasuje"
          description="Zmień albo wyczyść filtry."
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Wyczyść filtry
            </Button>
          }
          className="py-10"
        />
      ) : (
        <EmptyState icon={Inbox} title={text.title} description={text.description} className="py-10" />
      );
  } else {
    const items = list.data.items;
    body = (
      <div>
        {INBOX_GROUPS.map((group) => {
          const inGroup = items.filter((i) => i.group === group);
          if (inGroup.length === 0) return null;
          return (
            <section key={group} aria-label={INBOX_GROUP_LABELS[group]}>
              {filters.filter !== 'done' && <GroupLabel group={group} />}
              <div className="flex flex-col md:gap-0.5">
                {inGroup.map((item) => (
                  <InboxRow
                    key={item.id}
                    item={item}
                    active={item.id === selectedId}
                    to={itemPath(item.id)}
                    now={now}
                  />
                ))}
              </div>
            </section>
          );
        })}
        {list.data.nextCursor && (
          <div className="pt-3 md:px-3">
            <Button
              variant="ghost"
              size="s"
              className={TOUCH_BUTTON}
              loading={list.isFetching}
              onClick={() => setLimit((l) => Math.min(l + PAGE, 200))}
            >
              Pokaż starsze
            </Button>
          </div>
        )}
      </div>
    );
  }

  const snoozedCount = counts.data?.snoozed ?? 0;
  const phone = useIsPhone();
  /** Pola filtrów: na desktopie w panelu nad listą (`compact`), na telefonie w dolnym panelu (lifting 17.10.2026). */
  const filterFields = (compact: boolean) => (
    <>
      <Field label="Poczta" hint={filters.importance === 'important' ? 'Bez maili „Mniej ważne”.' : undefined}>
        <Segmented<InboxImportance>
          aria-label="Poczta: istotne czy wszystkie"
          size={compact ? 's' : 'm'}
          value={filters.importance}
          onValueChange={(v) => setParam('poczta', v === 'important' ? 'istotne' : null)}
          options={INBOX_IMPORTANCE_FILTERS.map((v) => ({ value: v, label: INBOX_IMPORTANCE_LABELS[v] }))}
          className="self-start"
        />
      </Field>
      <Field label="Ważność">
        <Select
          size={compact ? 's' : 'm'}
          value={filters.severity ?? ALL}
          onValueChange={(v) => setParam('waznosc', v === ALL ? null : v)}
          options={[
            { value: ALL, label: 'Wszystkie (bez akceptacji i „Do wiadomości”)' },
            ...FILTER_SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABELS[s] })),
          ]}
        />
      </Field>
      {access.can('clients') && (
        <Field label="Klient">
          <ClientPicker
            size={compact ? 's' : 'm'}
            value={filters.clientId}
            onChange={(id) => setParam('klient', id)}
            allowNone
            noneLabel="Wszyscy klienci"
          />
        </Field>
      )}
      <Field label="Źródło">
        <Select
          size={compact ? 's' : 'm'}
          value={filters.source ?? ALL}
          onValueChange={(v) => setParam('zrodlo', v === ALL ? null : v)}
          options={[
            { value: ALL, label: 'Wszystkie źródła' },
            ...EVENT_SOURCES.map((s) => ({ value: s, label: EVENT_SOURCE_LABELS[s] })),
          ]}
        />
      </Field>
      {compact && extra > 0 && (
        <div>
          <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={clearFilters}>
            Wyczyść filtry
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 pb-4 md:pl-3">
        <Heading className="min-w-0 flex-1 text-h2 text-fg md:text-h3">Inbox</Heading>
        <IconButton
          icon={SlidersHorizontal}
          label={extra > 0 ? `Filtry (aktywne: ${extra})` : 'Filtry'}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((o) => !o)}
          className={cn('max-md:size-11', extra > 0 && 'bg-accent-subtle text-accent')}
        />
        <Menu>
          <MenuTrigger asChild>
            <IconButton icon={MoreHorizontal} label="Więcej akcji Inboxu" className="max-md:size-11" />
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem
              disabled={unreadCount === 0}
              onSelect={() =>
                actions.markAllRead.mutate({
                  ...(filters.importance === 'important' ? { importance: 'important' as const } : {}),
                  ...(filters.severity ? { severity: filters.severity } : {}),
                  ...(filters.clientId ? { clientId: filters.clientId } : {}),
                  ...(filters.source ? { source: filters.source } : {}),
                })
              }
            >
              Oznacz wszystkie jako przeczytane
            </MenuItem>
            {menu}
          </MenuContent>
        </Menu>
      </div>

      <ApprovalsBanner />

      <Segmented<InboxFilter>
        aria-label="Widok Inboxu"
        size="s"
        fullWidth
        value={segmentValue}
        onValueChange={setView}
        options={(['open', 'unread', 'done'] as const).map((f) => ({
          value: f,
          label: INBOX_FILTER_LABELS[f],
        }))}
        className="mt-1 max-md:[&>button]:h-10"
      />

      {filtersOpen && phone && (
        <Sheet
          open
          onOpenChange={setFiltersOpen}
          side="bottom"
          title="Filtry"
          footer={
            <>
              {extra > 0 && (
                <Button variant="ghost" size="l" onClick={clearFilters} className="mr-auto">
                  Wyczyść
                </Button>
              )}
              <Button variant="primary" size="l" onClick={() => setFiltersOpen(false)}>
                Pokaż
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4 [&_[role=combobox]]:h-11 [&_button]:min-h-11">{filterFields(false)}</div>
        </Sheet>
      )}
      {filtersOpen && !phone && (
        <div className="mt-3 grid gap-3 rounded-lg border border-line bg-panel p-3 md:border-0 md:bg-surface">
          {filterFields(true)}
        </div>
      )}

      {filters.filter === 'snoozed' && (
        <div className="mt-3 flex items-center justify-between gap-2 text-body-s md:px-3">
          <span className="font-medium text-fg">{INBOX_FILTER_LABELS.snoozed}</span>
          <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={() => setView('open')}>
            Wróć do wszystkich
          </Button>
        </div>
      )}

      {/* telefon: lista w białym bloku na szarym tle (jak ui/Section); desktop: cała kolumna listy jest biała */}
      <div className="fos-section mt-2 max-md:rounded-xl max-md:border max-md:border-line max-md:bg-panel max-md:px-4">
        {body}
      </div>

      {filters.filter !== 'snoozed' && snoozedCount > 0 && (
        <div className="pt-4 md:px-3">
          <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={() => setView('snoozed')}>
            {snoozedCountLabel(snoozedCount)} — pokaż
          </Button>
        </div>
      )}
    </div>
  );
}
